<template>

    <div class="quick-actions">
        <button @click="selectAllProducts" class="quick-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 12l2 2 4-4"></path>
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            </svg>
            Inventaire complet
        </button>
        <button @click="selectByCategory" class="quick-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
            Par catégorie
        </button>
        <button @click="selectLowStock" class="quick-btn">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z">
                </path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            Stock faible
        </button>
    </div>

    <div v-if="showCategorySelection" class="selection-panel">
        <div class="panel-header">
            <h2>Sélectionner les catégories</h2>
            <button @click="showCategorySelection = false" class="close-btn">×</button>
        </div>

        <div class="search-bar">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input v-model="categorySearch" @input="handleCategorySearch" type="text"
                placeholder="Rechercher une catégorie..." />
        </div>

        <div class="categories-grid">
            <div v-for="category in categories" :key="category.id"
                :class="['category-item', { 'selected': selectedCategories.includes(category.id) }]"
                @click="toggleCategory(category.id)">
                <div class="category-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="3" y="3" width="7" height="7"></rect>
                    </svg>
                </div>
                <div class="category-info">
                    <h4>{{ category.name }}</h4>
                    <span>{{ category.product_count || 0 }} produits</span>
                </div>
                <div class="category-check">
                    <svg v-if="selectedCategories.includes(category.id)" width="18" height="18" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                </div>
            </div>
        </div>

        <div class="panel-actions">
            <button @click="loadProductsByCategories" :disabled="selectedCategories.length === 0" class="btn-primary">
                Continuer avec {{ selectedCategories.length }} catégorie(s)
            </button>
        </div>
    </div>

    <div class="main-content">
        <div class="content-header">
            <div class="search-section">
                <div class="search-bar">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="11" cy="11" r="8"></circle>
                        <path d="m21 21-4.35-4.35"></path>
                    </svg>
                    <input v-model="productSearch" @input="handleProductSearch" type="text"
                        placeholder="Rechercher un produit par nom ou SKU..." />
                </div>

                <div class="filters">
                    <select v-model="stockFilter" @change="applyFilters" class="filter-select">
                        <option value="">Tous les stocks</option>
                        <option value="in-stock">En stock</option>
                        <option value="low-stock">Stock faible</option>
                        <option value="out-of-stock">Rupture</option>
                    </select>

                    <select v-model="selectedCategoryFilter" @change="applyFilters" class="filter-select">
                        <option value="">Toutes catégories</option>
                        <option v-for="category in allCategories" :key="category.id" :value="category.id">
                            {{ category.name }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="selection-info">
                <span class="selection-count">{{ selectedProducts.length }} / {{ totalProducts }} produits
                    sélectionnés</span>
                <div class="bulk-actions">
                    <button @click="selectAllVisible" class="bulk-btn">Tout sélectionner</button>
                    <button @click="clearSelection" class="bulk-btn">Tout désélectionner</button>
                </div>
            </div>
        </div>

        <div v-if="loadingProducts" class="loading">
            <div class="spinner"></div>
            <p>Chargement des produits...</p>
        </div>

        <div v-else class="products-grid">
            <div v-for="product in products" :key="product.id"
                :class="['product-card', { 'selected': selectedProducts.includes(product.id) }]"
                @click="toggleProduct(product.id)">

                <div class="product-header">
                    <div class="product-selection">
                        <div class="checkbox">
                            <svg v-if="selectedProducts.includes(product.id)" width="16" height="16" viewBox="0 0 24 24"
                                fill="none" stroke="currentColor" stroke-width="2">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        </div>
                    </div>
                    <span class="product-sku">{{ product.sku || 'N/A' }}</span>
                </div>

                <div class="product-body">
                    <h4 class="product-name">{{ product.name }}</h4>
                    <p class="product-category">{{ product.category?.name || 'Sans catégorie' }}</p>

                    <div class="product-stock">
                        <span :class="['stock-badge', getStockClass(product)]">
                            {{ product.stock_quantity || 0 }} {{ product.unit || '' }}
                        </span>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="!loadingProducts && products.length === 0" class="empty-state">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
            </svg>
            <h3>Aucun produit trouvé</h3>
            <p>Essayez de modifier vos critères de recherche</p>
        </div>
    </div>

    <div class="floating-summary" v-show="selectedProducts.length > 0">
        <div class="summary-content">
            <div class="summary-info">
                <span class="count">{{ selectedProducts.length }} produits sélectionnés</span>
                <span class="total-stock">Stock total: {{ totalSelectedStock }}</span>
            </div>
            <button @click="createInventory" :disabled="isCreating" class="create-btn">
                <span v-if="isCreating">Création...</span>
                <span v-else>Créer l'inventaire</span>
            </button>
        </div>
    </div>

    <div v-if="showSuccess" class="success-modal">
        <div class="modal-content">
            <div class="success-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
            </div>
            <h2>Inventaire créé !</h2>
            <p>Référence: <strong>{{ createdInventory?.reference_number }}</strong></p>
            <div class="modal-actions">
                <button @click="goToInventoryHistory" class="btn-secondary">Voir les inventaires</button>
                <button @click="startInventory" class="btn-primary">Commencer le comptage</button>
            </div>
        </div>
    </div>
</template>

<script>
import categoriesService from '@/services/categoriesService';
import productsService from '@/services/productsService';
import physicalInventoryService from '@/services/physicalInventoryService';
import { useToastStore } from '@/store/modules/toastStore';

export default {
    name: 'CreateInventory',
    setup() {
        const toastStore = useToastStore();
        return { toastStore };
    },
    data() {
        return {
            showCategorySelection: false,
            showSuccess: false,

            categories: [],
            allCategories: [],
            selectedCategories: [],
            categorySearch: '',
            categorySearchTimeout: null,
            loadingCategories: false,

            products: [],
            allProducts: [],
            selectedProducts: [],
            productSearch: '',
            productSearchTimeout: null,
            loadingProducts: false,
            stockFilter: '',
            selectedCategoryFilter: '',
            totalProducts: 0,

            isCreating: false,
            createdInventory: null
        }
    },
    computed: {
        totalSelectedStock() {
            return this.allProducts
                .filter(p => this.selectedProducts.includes(p.id))
                .reduce((total, product) => total + (product.stock_quantity || 0), 0);
        }
    },
    async mounted() {
        await this.loadAllCategories();
        await this.loadProducts();
    },
    methods: {
        async loadAllCategories() {
            try {
                const response = await categoriesService.getCategories({ having_products: 1 });
                this.allCategories = response.data?.data || [];
                this.categories = [...this.allCategories];
            } catch (error) {
                console.error('Erreur chargement catégories:', error);
            }
        },

        async loadProducts() {
            this.loadingProducts = true;
            try {
                const params = {
                    per_page: 100,
                    search: this.productSearch || undefined,
                    categories: this.selectedCategoryFilter ? [this.selectedCategoryFilter] : undefined
                };

                const response = await productsService.getProducts(params);
                let products = response.data?.data || [];

                if (this.stockFilter) {
                    products = products.filter(product => {
                        const qty = product.stock_quantity || 0;
                        switch (this.stockFilter) {
                            case 'in-stock': return qty > (product.minimum_stock || 5);
                            case 'low-stock': return qty > 0 && qty <= (product.minimum_stock || 5);
                            case 'out-of-stock': return qty === 0;
                            default: return true;
                        }
                    });
                }

                this.products = products;
                this.totalProducts = products.length;
                this.allProducts = [...products];
            } catch (error) {
                console.error('Erreur chargement produits:', error);
            } finally {
                this.loadingProducts = false;
            }
        },

        handleCategorySearch() {
            clearTimeout(this.categorySearchTimeout);
            this.categorySearchTimeout = setTimeout(() => {
                if (this.categorySearch) {
                    this.categories = this.allCategories.filter(cat =>
                        cat.name.toLowerCase().includes(this.categorySearch.toLowerCase())
                    );
                } else {
                    this.categories = [...this.allCategories];
                }
            }, 300);
        },

        handleProductSearch() {
            clearTimeout(this.productSearchTimeout);
            this.productSearchTimeout = setTimeout(() => {
                this.loadProducts();
            }, 300);
        },

        applyFilters() {
            this.loadProducts();
        },

        selectAllProducts() {
            this.selectedProducts = [];
            this.stockFilter = '';
            this.selectedCategoryFilter = '';
            this.productSearch = '';
            this.loadProducts().then(() => {
                this.selectedProducts = this.products.map(p => p.id);
            });
        },

        selectByCategory() {
            this.selectedCategories = [];
            this.showCategorySelection = true;
        },

        selectLowStock() {
            this.selectedProducts = [];
            this.stockFilter = 'low-stock';
            this.loadProducts().then(() => {
                this.selectedProducts = this.products.map(p => p.id);
            });
        },

        toggleCategory(categoryId) {
            const index = this.selectedCategories.indexOf(categoryId);
            if (index > -1) {
                this.selectedCategories.splice(index, 1);
            } else {
                this.selectedCategories.push(categoryId);
            }
        },

        async loadProductsByCategories() {
            this.showCategorySelection = false;
            this.selectedCategoryFilter = this.selectedCategories[0] || '';
            await this.loadProducts();
            this.selectedProducts = this.products.map(p => p.id);
        },

        toggleProduct(productId) {
            const index = this.selectedProducts.indexOf(productId);
            if (index > -1) {
                this.selectedProducts.splice(index, 1);
            } else {
                this.selectedProducts.push(productId);
            }
        },

        selectAllVisible() {
            const visibleIds = this.products.map(p => p.id);
            visibleIds.forEach(id => {
                if (!this.selectedProducts.includes(id)) {
                    this.selectedProducts.push(id);
                }
            });
        },

        clearSelection() {
            this.selectedProducts = [];
        },

        getStockClass(product) {
            const qty = product.stock_quantity || 0;
            if (qty === 0) return 'out-of-stock';
            if (qty <= (product.minimum_stock || 5)) return 'low-stock';
            return 'in-stock';
        },

        async createInventory() {
            if (this.selectedProducts.length === 0 || this.isCreating) return;

            this.isCreating = true;
            try {
                const response = await physicalInventoryService.createInventory({
                    product_ids: this.selectedProducts
                });

                this.createdInventory = response.inventory;
                this.showSuccess = true;

                this.toastStore.addToast({
                    type: 'success',
                    message: 'Inventaire créé avec succès'
                });
            } catch (error) {
                console.error('Erreur création inventaire:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la création'
                });
            } finally {
                this.isCreating = false;
            }
        },

        goToInventoryHistory() {
            this.$router.push({ name: 'inventory-history' });
        },

        startInventory() {
            if (this.createdInventory?.id) {
                this.$router.push({
                    name: 'inventory-count',
                    params: { id: this.createdInventory.id }
                });
            }
        }
    }
}
</script>
<style scoped>
.create-inventory {
    padding: 24px;
    max-width: 1400px;
    margin: 0 auto;
    min-height: 100vh;
    background: #f8fafc;
}

.page-header {
    margin-bottom: 32px;
    text-align: center;
}

.page-title {
    font-size: 32px;
    font-weight: 700;
    color: #0A5C6B;
    margin-bottom: 8px;
}

.page-subtitle {
    font-size: 16px;
    color: #64748b;
}

.quick-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
    margin-bottom: 32px;
}

.quick-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 24px;
    background: white;
    border: 2px solid #0A5C6B;
    border-radius: 12px;
    color: #0A5C6B;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 14px;
}

.quick-btn:hover {
    background: #0A5C6B;
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(10, 92, 107, 0.2);
}

.selection-panel {
    background: white;
    border-radius: 16px;
    padding: 32px;
    margin-bottom: 32px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border: 1px solid #e2e8f0;
}

.panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
}

.panel-header h2 {
    font-size: 20px;
    font-weight: 600;
    color: #0A5C6B;
}

.close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: #f1f5f9;
    border-radius: 8px;
    cursor: pointer;
    font-size: 18px;
    color: #64748b;
    transition: all 0.2s;
}

.close-btn:hover {
    background: #e2e8f0;
    color: #475569;
}

.search-bar {
    position: relative;
    margin-bottom: 24px;
}

.search-bar svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-bar input {
    width: 100%;
    padding: 14px 16px 14px 48px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-size: 14px;
    transition: all 0.2s;
    background: white;
}

.search-bar input:focus {
    outline: none;
    border-color: #0A5C6B;
    box-shadow: 0 0 0 3px rgba(10, 92, 107, 0.1);
}

.categories-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 16px;
    margin-bottom: 24px;
}

.category-item {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    background: white;
}

.category-item:hover {
    border-color: #0A5C6B;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 92, 107, 0.1);
}

.category-item.selected {
    border-color: #0A5C6B;
    background: #d1fae5;
}

.category-icon {
    width: 48px;
    height: 48px;
    background: #f0fdfa;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0A5C6B;
}

.category-info {
    flex: 1;
}

.category-info h4 {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 4px;
}

.category-info span {
    font-size: 13px;
    color: #64748b;
}

.category-check {
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background: #0A5C6B;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s;
}

.category-item.selected .category-check {
    opacity: 1;
}

.panel-actions {
    text-align: center;
}

.main-content {
    background: white;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    margin-bottom: 100px;
}

.content-header {
    margin-bottom: 32px;
}

.search-section {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
}

.search-section .search-bar {
    flex: 1;
    margin-bottom: 0;
}

.filters {
    display: flex;
    gap: 12px;
}

.filter-select {
    padding: 12px 16px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    background: white;
    color: #475569;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 150px;
}

.filter-select:focus {
    outline: none;
    border-color: #0A5C6B;
}

.selection-info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 20px;
    border-bottom: 1px solid #e2e8f0;
}

.selection-count {
    font-weight: 600;
    color: #0A5C6B;
    font-size: 16px;
}

.bulk-actions {
    display: flex;
    gap: 8px;
}

.bulk-btn {
    padding: 8px 16px;
    border: 1px solid #e2e8f0;
    background: white;
    color: #475569;
    border-radius: 6px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    transition: all 0.2s;
}

.bulk-btn:hover {
    background: #f8fafc;
    border-color: #0A5C6B;
    color: #0A5C6B;
}

.loading {
    text-align: center;
    padding: 80px 20px;
}

.spinner {
    width: 48px;
    height: 48px;
    border: 4px solid #e2e8f0;
    border-top-color: #0A5C6B;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.products-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 16px;
}

.product-card {
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.2s;
    background: white;
}

.product-card:hover {
    border-color: #0A5C6B;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(10, 92, 107, 0.1);
}

.product-card.selected {
    border-color: #0A5C6B;
    background: #d1fae5;
}

.product-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.checkbox {
    width: 20px;
    height: 20px;
    border: 2px solid #d1d5db;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.product-card.selected .checkbox {
    background: #0A5C6B;
    border-color: #0A5C6B;
    color: white;
}

.product-sku {
    font-size: 11px;
    padding: 4px 8px;
    background: #f1f5f9;
    border-radius: 6px;
    color: #64748b;
    font-weight: 500;
}

.product-name {
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 6px;
    line-height: 1.4;
}

.product-category {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 12px;
}

.stock-badge {
    font-size: 12px;
    font-weight: 600;
    padding: 6px 12px;
    border-radius: 20px;
}

.stock-badge.in-stock {
    background: #dcfce7;
    color: #065f46;
}

.stock-badge.low-stock {
    background: #fef3c7;
    color: #92400e;
}

.stock-badge.out-of-stock {
    background: #fee2e2;
    color: #dc2626;
}

.empty-state {
    text-align: center;
    padding: 80px 20px;
    color: #94a3b8;
}

.empty-state svg {
    margin-bottom: 16px;
}

.empty-state h3 {
    font-size: 18px;
    margin-bottom: 8px;
    color: #64748b;
}

.floating-summary {
    position: fixed;
    bottom: 24px;
    left: 50%;
    transform: translateX(-50%);
    background: white;
    border: 2px solid #0A5C6B;
    border-radius: 16px;
    box-shadow: 0 10px 40px rgba(10, 92, 107, 0.2);
    z-index: 1000;
    animation: slideUp 0.3s ease;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translate(-50%, 100%);
    }

    to {
        opacity: 1;
        transform: translate(-50%, 0);
    }
}

.summary-content {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 20px 32px;
}

.summary-info {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.count {
    font-weight: 700;
    color: #0A5C6B;
    font-size: 16px;
}

.total-stock {
    font-size: 13px;
    color: #64748b;
}

.create-btn {
    padding: 12px 32px;
    background: #0A5C6B;
    color: white;
    border: none;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
}

.create-btn:hover:not(:disabled) {
    background: #094854;
    transform: translateY(-1px);
}

.create-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.success-modal {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000;
    animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }

    to {
        opacity: 1;
    }
}

.modal-content {
    background: white;
    border-radius: 20px;
    padding: 40px;
    text-align: center;
    max-width: 480px;
    margin: 20px;
    animation: scaleIn 0.3s ease;
}

@keyframes scaleIn {
    from {
        opacity: 0;
        transform: scale(0.9);
    }

    to {
        opacity: 1;
        transform: scale(1);
    }
}

.success-icon {
    width: 80px;
    height: 80px;
    background: #dcfce7;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    color: #065f46;
}

.modal-content h2 {
    font-size: 24px;
    font-weight: 700;
    color: #0A5C6B;
    margin-bottom: 12px;
}

.modal-content p {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 32px;
}

.modal-content strong {
    color: #0A5C6B;
    font-weight: 600;
}

.modal-actions {
    display: flex;
    gap: 16px;
    justify-content: center;
}

.btn-primary,
.btn-secondary {
    padding: 12px 24px;
    border-radius: 10px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    font-size: 14px;
}

.btn-primary {
    background: #0A5C6B;
    color: white;
}

.btn-primary:hover {
    background: #094854;
    transform: translateY(-1px);
}

.btn-secondary {
    background: white;
    color: #64748b;
    border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
}

@media (max-width: 768px) {
    .create-inventory {
        padding: 16px;
    }

    .quick-actions {
        flex-direction: column;
        align-items: center;
    }

    .search-section {
        flex-direction: column;
    }

    .filters {
        justify-content: center;
    }

    .products-grid {
        grid-template-columns: 1fr;
    }

    .summary-content {
        flex-direction: column;
        text-align: center;
        gap: 16px;
    }

    .modal-actions {
        flex-direction: column;
    }
}
</style>